// ═══════════════════════════════════════════════════════════
// Live Podcasts — Frontend Application
// Event-driven podcast debate viewer with avatar support
// and setup flow.
// ═══════════════════════════════════════════════════════════

import { avatarManager } from './avatar-manager.ts';
import { PERSONAS } from '../personas.ts';
import type { Persona, PodcastEvent, SetupEvent, SpeakerRole, CameraMode, StancePair, DebateConfig } from '../types/index.ts';

// ═══════════════════════════════════════════════════════════
// State
// ═══════════════════════════════════════════════════════════

let speakers: SetupEvent | null = null;
let eventSource: EventSource | null = null;
let avatarsEnabled = false;
let selectedTopic: string | null = null;
let selectedContext: string = '';
let _currentCamera: CameraMode | string = 'three_up';
let captionsEnabled = false;
let captionRevealTimer: ReturnType<typeof setTimeout> | ReturnType<typeof setInterval> | null = null;
let titleFlashInterval: ReturnType<typeof setInterval> | null = null;
let titleFlashTimeout: ReturnType<typeof setTimeout> | null = null;

// Persona selection state
const selectedPersonas: { host: string | null; debaterA: string | null; debaterB: string | null } = { host: null, debaterA: null, debaterB: null };
let selectedStancePair: StancePair | null = null;
let personaTags: Record<string, string> = {};
let personaProfessions: Record<string, string> = {};
let draggedPersonaId: string | null = null;
let currentCategory = 'trending';
let shareSlug: string | null = null;

// Loading cycle state
let loadingCycleTimer: ReturnType<typeof setInterval> | null = null;
let loadingStepIdx = 0;
const LOADING_STEPS = [
  'Setting up the camera...',
  'Adjusting the lights...',
  'Reviewing the briefing notes...',
  'Warming up the mics...',
  'Almost ready...',
];

// Event queue state
const eventQueue: PodcastEvent[] = [];
let processing = false;
let _queueTimer: ReturnType<typeof setTimeout> | null = null;

// ═══════════════════════════════════════════════════════════
// Browser TTS Fallback — speaks when LiveAvatars aren't available
// ═══════════════════════════════════════════════════════════

const tts = {
  voices: null as SpeechSynthesisVoice[] | null,
  voiceMap: {} as Record<string, SpeechSynthesisVoice | null>,

  init() {
    if (!window.speechSynthesis) return;
    const assign = () => {
      const all = speechSynthesis.getVoices();
      if (all.length === 0) return;
      const en = all.filter((v) => v.lang.startsWith('en'));
      const pool = en.length >= 3 ? en : all;
      this.voiceMap.Host = pool[0] || null;
      this.voiceMap.DebaterA = pool[1] || pool[0] || null;
      this.voiceMap.DebaterB = pool[2] || pool[0] || null;
      this.voices = pool;
    };
    assign();
    if (!this.voices || this.voices.length === 0) {
      speechSynthesis.addEventListener('voiceschanged', assign, { once: true });
    }
  },

  speak(speakerKey: string, text: string): Promise<void> {
    return new Promise((resolve) => {
      if (!window.speechSynthesis) { resolve(); return; }

      speechSynthesis.cancel();

      const utter = new SpeechSynthesisUtterance(text);
      const voice = this.voiceMap[speakerKey];
      if (voice) utter.voice = voice;

      if (speakerKey === 'Host') {
        utter.pitch = 0.9;
        utter.rate = 1.05;
      } else if (speakerKey === 'DebaterA') {
        utter.pitch = 1.1;
        utter.rate = 1.0;
      } else {
        utter.pitch = 0.8;
        utter.rate = 0.95;
      }

      utter.onend = () => resolve();
      utter.onerror = () => resolve();

      const timeout = setTimeout(() => {
        speechSynthesis.cancel();
        resolve();
      }, 30000);
      utter.onend = () => { clearTimeout(timeout); resolve(); };
      utter.onerror = () => { clearTimeout(timeout); resolve(); };

      speechSynthesis.speak(utter);
    });
  },
};

tts.init();

// ═══════════════════════════════════════════════════════════
// DOM Helpers
// ═══════════════════════════════════════════════════════════

const $ = (sel: string) => document.querySelector<HTMLElement>(sel);
const $$ = (sel: string) => document.querySelectorAll<HTMLElement>(sel);

function stripMarkdown(text: string): string {
  let cleaned = text.replace(/\*\*(.+?)\*\*/g, '$1');
  cleaned = cleaned.replace(/\*[^*]+?\*/g, '');
  cleaned = cleaned.replace(/\[([^\]]+)\]\s*/g, '');
  return cleaned.trim();
}

function updateCaption(speakerKey: string, name: string, text: string): void {
  const initialsEl = $('#lt-caption-initials');
  const accentEl = $('#lt-caption-accent');
  const textEl = $('#lt-caption-text');
  if (!initialsEl || !textEl) return;

  if (captionRevealTimer) { clearTimeout(captionRevealTimer as ReturnType<typeof setTimeout>); clearInterval(captionRevealTimer as ReturnType<typeof setInterval>); captionRevealTimer = null; }

  const color = speakerKey === 'Host'
    ? 'var(--color-host)'
    : speakerKey === 'DebaterA'
      ? 'var(--color-for)'
      : 'var(--color-against)';

  const initials = name.split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase();
  initialsEl.textContent = initials;
  initialsEl.style.color = color;
  if (accentEl) accentEl.style.background = color;

  const words = text.split(/\s+/).filter(Boolean);
  if (!words.length) { textEl.textContent = ''; return; }

  const ttsLeadIn = avatarsEnabled ? 600 : 100;
  const msPerWord = 320;
  let idx = 0;
  textEl.innerHTML = '';

  captionRevealTimer = setTimeout(() => {
    captionRevealTimer = setInterval(() => {
      if (idx >= words.length) {
        clearInterval(captionRevealTimer as ReturnType<typeof setInterval>);
        captionRevealTimer = null;
        const last = textEl.querySelector('.caption-word-current');
        if (last) { last.classList.remove('caption-word-current'); last.classList.add('caption-word-done'); }
        return;
      }

      const prev = textEl.querySelector('.caption-word-current');
      if (prev) { prev.classList.remove('caption-word-current'); prev.classList.add('caption-word-done'); }

      const span = document.createElement('span');
      span.className = 'caption-word caption-word-current';
      span.textContent = (idx > 0 ? ' ' : '') + words[idx];
      textEl.appendChild(span);
      idx++;

      textEl.scrollTop = textEl.scrollHeight;
    }, msPerWord);
  }, ttsLeadIn);
}

// ═══════════════════════════════════════════════════════════
// Event Delays & Skippable Set
// ═══════════════════════════════════════════════════════════

const EVENT_DELAYS: Record<string, number> = {
  camera: 0,
  setup: 2000,
  host_speak: 4000,
  debater_speak: 5000,
  thinking: 0,
  interject: 3000,
  outro: 0,
  done: 0,
  error: 0,
};

// ═══════════════════════════════════════════════════════════
// Camera Cycle — rotate through compositions for testing
// ═══════════════════════════════════════════════════════════

interface CameraPreset {
  label: string;
  mode: CameraMode | null;
  subjects?: SpeakerRole[];
}

const CAMERA_PRESETS: CameraPreset[] = [
  { label: 'auto', mode: null },
  { label: 'three_up', mode: 'three_up', subjects: ['Host', 'DebaterA', 'DebaterB'] },
  { label: 'focus', mode: 'three_up_focus', subjects: ['Host', 'DebaterA', 'DebaterB'] },
  { label: 'split', mode: 'split', subjects: ['DebaterA', 'DebaterB'] },
  { label: 'split zoom', mode: 'split_zoom', subjects: ['DebaterA', 'DebaterB'] },
  { label: 'solo framed', mode: 'solo_framed', subjects: ['Host'] },
  { label: 'solo full', mode: 'solo_fullbleed', subjects: ['Host'] },
  { label: 'solo A', mode: 'solo_framed', subjects: ['DebaterA'] },
  { label: 'solo B', mode: 'solo_framed', subjects: ['DebaterB'] },
];
let cameraPresetIdx = 0;
let manualCameraOverride = false;

function selectCameraPreset(idx: number): void {
  cameraPresetIdx = idx;
  const preset = CAMERA_PRESETS[cameraPresetIdx];
  const label = $('#camera-label');
  const btn = $('#camera-btn');

  if (preset.mode === null) {
    manualCameraOverride = false;
    if (label) label.textContent = 'auto';
    if (btn) btn.classList.remove('manual');
  } else {
    manualCameraOverride = true;
    setCameraMode(preset.mode, preset.subjects);
    if (label) label.textContent = preset.label;
    if (btn) btn.classList.add('manual');
  }
}

// ═══════════════════════════════════════════════════════════
// Video Tile Helpers
// ═══════════════════════════════════════════════════════════

function getTile(name: string): HTMLElement | null {
  return $(`.video-tile[data-speaker="${name}"]`);
}

function setActiveSpeaker(name: string | null): void {
  $$('.video-tile').forEach((t) => {
    t.classList.remove('active-speaker', 'dimmed');
    if (name && t.dataset.speaker !== name) t.classList.add('dimmed');
  });
  if (name) {
    const tile = getTile(name);
    if (tile) tile.classList.add('active-speaker');
  }
}

function clearSpeakerStates(): void {
  $$('.video-tile').forEach((t) => {
    t.classList.remove('active-speaker', 'dimmed', 'thinking');
  });
}

// ═══════════════════════════════════════════════════════════
// Camera Composition System
// ═══════════════════════════════════════════════════════════

function setCameraMode(mode: CameraMode | string, subjects?: SpeakerRole[]): void {
  const strip = $('#video-strip');
  if (!strip) return;

  _currentCamera = mode;
  strip.setAttribute('data-camera', mode);

  const allSpeakers: SpeakerRole[] = ['Host', 'DebaterA', 'DebaterB'];
  const visibleSet = subjects ? new Set(subjects) : new Set(allSpeakers);

  allSpeakers.forEach((name) => {
    const tile = getTile(name);
    if (tile) {
      tile.setAttribute('data-visible', visibleSet.has(name) ? 'true' : 'false');
    }
  });
}

function setThinking(name: string, isThinking: boolean): void {
  const tile = getTile(name);
  if (!tile) return;
  if (isThinking) tile.classList.add('thinking');
  else tile.classList.remove('thinking');
}

// ═══════════════════════════════════════════════════════════
// Dial-in Animation
// ═══════════════════════════════════════════════════════════

function delayMs(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}

async function dialIn(): Promise<void> {
  const tiles = Array.from($$('.video-tile'));

  for (const tile of tiles) {
    tile.classList.add('connecting');
    tile.classList.remove('connected');
  }

  for (let i = 0; i < tiles.length; i++) {
    await delayMs(600 + Math.random() * 400);
    tiles[i].classList.remove('connecting');
    if (avatarsEnabled) {
      tiles[i].classList.add('connected');
    }
  }
}

// ═══════════════════════════════════════════════════════════
// Loading Cycle — creative status messages while pre-gen runs
// ═══════════════════════════════════════════════════════════

function startLoadingCycle(): void {
  loadingStepIdx = 0;
  const label = $('#connecting-text');
  if (label) label.textContent = LOADING_STEPS[0];

  loadingCycleTimer = setInterval(() => {
    loadingStepIdx++;
    if (loadingStepIdx >= LOADING_STEPS.length) {
      loadingStepIdx = LOADING_STEPS.length - 1;
    }
    if (label) label.textContent = LOADING_STEPS[loadingStepIdx];
  }, 2500);
}

function stopLoadingCycle(): void {
  if (loadingCycleTimer) {
    clearInterval(loadingCycleTimer);
    loadingCycleTimer = null;
  }
}

// ═══════════════════════════════════════════════════════════
// Event Handlers
// ═══════════════════════════════════════════════════════════

const handlers: Record<string, (e: any) => void | Promise<void>> = { // eslint-disable-line @typescript-eslint/no-explicit-any
  setup(e: SetupEvent) {
    speakers = e;

    const { host: hostP, debaterA: debaterAP, debaterB: debaterBP } = getPersonasFromSelection();
    const photoUrl = (persona: Persona | undefined) => persona?.photo || `https://api.dicebear.com/9.x/notionists/svg?seed=${encodeURIComponent(persona?.name || 'unknown')}&backgroundColor=1a1a2e`;

    if (e.host) {
      const nameHost = $('#tile-host .nameplate-name');
      const profHost = $('#prof-host');
      const roleHost = $('#role-host');
      const fallbackHost = $('#tile-host .avatar-fallback');
      const faceHost = $('#face-host');
      if (nameHost) nameHost.textContent = e.host.name;
      if (profHost) profHost.textContent = personaProfessions[hostP?.id ?? ''] || '';
      if (roleHost) roleHost.textContent = 'HOST';
      if (fallbackHost) fallbackHost.textContent = e.host.name ? e.host.name.split(' ').map((w) => w[0]).join('') : 'H';
      if (faceHost) (faceHost as unknown as HTMLImageElement).src = photoUrl(hostP);
    }

    if (e.debaterA) {
      const nameA = $('#name-a');
      const profA = $('#prof-a');
      const roleA = $('#role-a');
      const fallbackA = $('#tile-a .avatar-fallback');
      const faceA = $('#face-a');
      if (nameA) nameA.textContent = e.debaterA.name;
      if (profA) profA.textContent = personaProfessions[debaterAP?.id ?? ''] || '';
      if (roleA) roleA.textContent = e.debaterA.position || 'FOR';
      if (fallbackA) fallbackA.textContent = e.debaterA.name ? e.debaterA.name.split(' ').map((w) => w[0]).join('') : '?';
      if (faceA) (faceA as unknown as HTMLImageElement).src = photoUrl(debaterAP);
    }

    if (e.debaterB) {
      const nameB = $('#name-b');
      const profB = $('#prof-b');
      const roleB = $('#role-b');
      const fallbackB = $('#tile-b .avatar-fallback');
      const faceB = $('#face-b');
      if (nameB) nameB.textContent = e.debaterB.name;
      if (profB) profB.textContent = personaProfessions[debaterBP?.id ?? ''] || '';
      if (roleB) roleB.textContent = e.debaterB.position || 'AGAINST';
      if (fallbackB) fallbackB.textContent = e.debaterB.name ? e.debaterB.name.split(' ').map((w) => w[0]).join('') : '?';
      if (faceB) (faceB as unknown as HTMLImageElement).src = photoUrl(debaterBP);
    }

    if (avatarsEnabled) {
      avatarManager.attachAll();
    }

    setCameraMode('three_up', ['Host', 'DebaterA', 'DebaterB']);

    const topicEl = $('#lt-topic-text');
    if (topicEl && selectedTopic) {
      topicEl.textContent = selectedTopic;
    }
    const ptbEl = $('#ptb-topic-text');
    if (ptbEl && selectedTopic) ptbEl.textContent = selectedTopic;
  },

  camera(e: { mode: CameraMode; subjects: SpeakerRole[] }) {
    if (manualCameraOverride) return;
    const mode = e.mode === 'solo' ? 'solo_framed' : e.mode;
    setCameraMode(mode, e.subjects);
  },

  host_speak(e: { text: string; emotion: string | null }) {
    const text = stripMarkdown(e.text);
    updateCaption('Host', speakers?.host?.name || 'Host', text);
    clearSpeakerStates();
    setActiveSpeaker('Host');

    if (!captionsEnabled) setTimeout(() => { if (!captionsEnabled) { toggleCaptions(); startTitleFlash(); } }, 2000);

    const onDone = () => {
      setActiveSpeaker(null);
    };

    if (avatarsEnabled) {
      return avatarManager.speak('Host', text).then(onDone);
    }

    return tts.speak('Host', text).then(onDone);
  },

  debater_speak(e: { speaker: SpeakerRole; name: string; text: string }) {
    const text = stripMarkdown(e.text);
    updateCaption(e.speaker, e.name, text);
    clearSpeakerStates();
    setActiveSpeaker(e.speaker);

    if (avatarsEnabled) {
      return avatarManager.speak(e.speaker, text);
    }

    return tts.speak(e.speaker, text);
  },

  generating(_e: { step: number; totalSteps: number }) {
    // Progress is shown via the loading cycle messages
  },

  generating_done() {
    stopLoadingCycle();
    const blocker = $('#connecting-blocker');
    if (blocker) blocker.classList.remove('active');
  },

  thinking(e: { speaker: string }) {
    setThinking(e.speaker, true);
    setActiveSpeaker(e.speaker);
  },

  interject(e: { text: string; emotion: string | null }) {
    const text = stripMarkdown(e.text);
    updateCaption('Host', speakers?.host?.name || 'Host', text);
    clearSpeakerStates();
    setActiveSpeaker('Host');

    const onDone = () => {
      setActiveSpeaker(null);
    };

    if (avatarsEnabled) {
      return avatarManager.speak('Host', text).then(onDone);
    }

    return tts.speak('Host', text).then(onDone);
  },

  outro(e: { topic: string; cliffhanger: string }) {
    clearSpeakerStates();
    stopTitleFlash();
    showOutro(e);
  },

  error(e: { message: string }) {
    console.error('Podcast error:', e.message);
  },

  done() {
    // Stream ended — nothing to do, events already queued
  },

  share_ready(e: { slug: string; url: string }) {
    shareSlug = e.slug;
  },
};

// ═══════════════════════════════════════════════════════════
// Outro Screen
// ═══════════════════════════════════════════════════════════

function showOutro(e: { topic: string; cliffhanger: string }): void {
  const overlay = $('#overlay');
  const content = $('#overlay-content');
  if (!overlay || !content) return;

  content.innerHTML = '';
  content.className = 'outro-screen';

  const title = document.createElement('div');
  title.className = 'outro-topic';
  title.textContent = e.topic;
  content.appendChild(title);

  const cliff = document.createElement('div');
  cliff.className = 'outro-cliffhanger';
  cliff.textContent = e.cliffhanger || `"${e.topic}"`;
  content.appendChild(cliff);

  const shareRow = document.createElement('div');
  shareRow.className = 'outro-share-row';

  const shareX = document.createElement('button');
  shareX.className = 'outro-share-btn';
  shareX.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg> Share on X';
  shareX.addEventListener('click', () => {
    const shareUrl = shareSlug
      ? `${window.location.origin}/replay/${shareSlug}`
      : window.location.href;
    const text = `Watch this AI debate: "${selectedTopic}"`;
    window.open(
      `https://x.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(shareUrl)}`,
      '_blank',
    );
  });

  const copyBtn = document.createElement('button');
  copyBtn.className = 'outro-share-btn';
  copyBtn.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg> Copy Link';
  copyBtn.addEventListener('click', async () => {
    const shareUrl = shareSlug
      ? `${window.location.origin}/replay/${shareSlug}`
      : window.location.href;
    try {
      await navigator.clipboard.writeText(shareUrl);
      showToast('Link copied!');
    } catch {
      showToast('Could not copy link');
    }
  });

  shareRow.appendChild(shareX);
  shareRow.appendChild(copyBtn);
  content.appendChild(shareRow);

  const cta = document.createElement('button');
  cta.className = 'outro-cta';
  cta.textContent = 'Design Another Debate';
  cta.addEventListener('click', () => { window.location.href = '/'; });
  content.appendChild(cta);

  overlay.style.display = 'flex';

  avatarManager.destroy().catch(console.error);
}

function showToast(message: string): void {
  let toast = $('#outro-toast');
  if (toast) toast.remove();

  toast = document.createElement('div');
  toast.id = 'outro-toast';
  toast.textContent = message;
  document.body.appendChild(toast);

  setTimeout(() => toast!.classList.add('visible'), 10);
  setTimeout(() => {
    toast!.classList.remove('visible');
    setTimeout(() => toast!.remove(), 300);
  }, 2000);
}

// ═══════════════════════════════════════════════════════════
// Event Queue
// ═══════════════════════════════════════════════════════════

function enqueueEvent(event: PodcastEvent): void {
  eventQueue.push(event);
  if (!processing) processNext();
}

async function processNext(): Promise<void> {
  if (eventQueue.length === 0) {
    processing = false;
    return;
  }

  processing = true;
  const event = eventQueue.shift()!;

  const handler = handlers[event.type];
  let speakPromise: void | Promise<void> | undefined;
  if (handler) speakPromise = handler(event);

  const baseDelay = EVENT_DELAYS[event.type] ?? 1500;

  if (baseDelay === 0 && !speakPromise) {
    processNext();
  } else {
    const delayPromise = baseDelay > 0
      ? new Promise<void>((r) => { _queueTimer = setTimeout(r, baseDelay); })
      : Promise.resolve();

    await Promise.all([delayPromise, speakPromise].filter(Boolean));
    processNext();
  }
}

// ═══════════════════════════════════════════════════════════
// Hot Topic Recommendations
// ═══════════════════════════════════════════════════════════

const FALLBACK_TOPICS = [
  "Should AI replace human artists?",
  "Is social media destroying democracy?",
  "Will remote work survive the return-to-office push?",
  "Should we regulate Big Tech like utilities?",
  "Is college still worth the investment?",
  "Should governments ban encryption backdoors?",
  "Is universal basic income inevitable?",
  "Are electric vehicles actually better for the planet?",
];

function getRandomFallbacks(): Array<{ question: string }> {
  const shuffled = [...FALLBACK_TOPICS].sort(() => Math.random() - 0.5);
  return shuffled.slice(0, 6).map((q) => ({ question: q }));
}

interface TopicCardData {
  question: string;
  sourceDomain?: string;
  sourceUrl?: string;
}

function renderTopicCards(topics: TopicCardData[]): void {
  const cards = $$('.topic-card');
  cards.forEach((card, i) => {
    const topic = topics[i];
    if (!topic) {
      card.style.display = 'none';
      return;
    }
    card.style.display = '';
    card.classList.remove('loading');

    const textEl = card.querySelector('.topic-card-text');
    if (textEl) textEl.textContent = topic.question;

    const sourceEl = card.querySelector('.topic-card-source');
    if (sourceEl) sourceEl.textContent = topic.sourceDomain || '';

    card.dataset.sourceUrl = topic.sourceUrl || '';
    card.dataset.sourceDomain = topic.sourceDomain || '';
  });
}

function selectTopicCard(idx: number): void {
  const cards = $$('.topic-card');
  cards.forEach((c) => c.classList.remove('selected'));

  const card = cards[idx];
  if (!card || card.classList.contains('loading')) return;

  card.classList.add('selected');
  const text = card.querySelector('.topic-card-text')?.textContent;
  const input = $('#custom-topic') as HTMLInputElement | null;
  if (input && text) input.value = text;

  const btn = $('#next-step-btn') as HTMLButtonElement | null;
  if (btn) btn.disabled = !text;
}

async function fetchAITopics(category: string = 'trending'): Promise<void> {
  try {
    const res = await fetch(`/api/topics?category=${category}`);
    if (!res.ok) {
      console.warn(`AI topics: HTTP ${res.status} — using fallbacks`);
      renderTopicCards(getRandomFallbacks());
      return;
    }
    const data = await res.json() as { topics?: TopicCardData[] };
    if (data.topics && data.topics.length >= 1) {
      renderTopicCards(data.topics);
    } else {
      console.warn('AI topics: empty response, using fallbacks');
      renderTopicCards(getRandomFallbacks());
    }
  } catch (err) {
    console.warn('AI topic fetch failed, using fallbacks:', err);
    renderTopicCards(getRandomFallbacks());
  }
}

function resetTopicCards(): void {
  const cards = $$('.topic-card');
  cards.forEach((card) => {
    card.style.display = '';
    card.classList.add('loading');
    card.classList.remove('selected');
    const sourceEl = card.querySelector('.topic-card-source');
    if (sourceEl) sourceEl.textContent = '';
    const textEl = card.querySelector('.topic-card-text');
    if (textEl) textEl.textContent = '\u00A0';
    card.dataset.sourceUrl = '';
    card.dataset.sourceDomain = '';
  });
}

async function refreshTopics(): Promise<void> {
  const btn = $('#refresh-topics-btn');
  if (btn) btn.classList.add('spinning');
  resetTopicCards();
  await fetchAITopics(currentCategory);
  if (btn) btn.classList.remove('spinning');
}

function initTopicCards(): void {
  $$('.topic-card').forEach((card, i) => {
    card.addEventListener('click', () => selectTopicCard(i));
  });

  $$('.topic-tab').forEach(tab => {
    tab.addEventListener('click', () => {
      $$('.topic-tab').forEach(t => t.classList.remove('active'));
      tab.classList.add('active');
      currentCategory = (tab as HTMLElement).dataset.category || 'trending';
      resetTopicCards();
      fetchAITopics(currentCategory);
    });
  });

  fetchAITopics(currentCategory);
}

// ═══════════════════════════════════════════════════════════
// URL Detection & Source Badge
// ═══════════════════════════════════════════════════════════

const URL_PATTERN = /^https?:\/\/[^\s]+$/i;

async function extractTopicFromUrl(url: string): Promise<void> {
  const input = $('#custom-topic') as HTMLInputElement | null;
  if (!input) return;

  input.classList.add('extracting');
  input.disabled = true;

  try {
    const res = await fetch('/api/topics/extract', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ url }),
    });
    const data = await res.json() as { question?: string };

    if (data.question) {
      input.value = data.question;
      const btn = $('#next-step-btn') as HTMLButtonElement | null;
      if (btn) btn.disabled = false;
    }
  } catch (err) {
    console.warn('URL extraction failed:', err);
  } finally {
    input.classList.remove('extracting');
    input.disabled = false;
    input.focus();
  }
}

// ═══════════════════════════════════════════════════════════
// Setup Flow — Step Navigation
// ═══════════════════════════════════════════════════════════

function showStep(step: number): void {
  const topicPicker = $('#topic-picker');
  const controlRoom = $('#control-room-picker');
  const backBtn = $('#back-step-btn');
  if (topicPicker) topicPicker.style.display = step === 1 ? '' : 'none';
  if (controlRoom) controlRoom.style.display = step === 2 ? '' : 'none';
  if (backBtn) backBtn.style.display = step > 1 ? '' : 'none';
}

// ═══════════════════════════════════════════════════════════
// Setup Flow — Stance Selection & Control Room
// ═══════════════════════════════════════════════════════════

async function goToStep2(): Promise<void> {
  const topic = ($('#custom-topic') as HTMLInputElement | null)?.value.trim();
  if (!topic) return;
  selectedTopic = topic;

  showStep(2);

  const chyron = $('#room-chyron');
  if (chyron) chyron.textContent = 'Loading casts...';

  const labelLeft = $('#monitor-label-left');
  const labelRight = $('#monitor-label-right');
  if (labelLeft) labelLeft.innerHTML = '&nbsp;';
  if (labelRight) labelRight.innerHTML = '&nbsp;';

  const pool = $('#persona-pool');
  const startBtn = $('#start-btn');
  if (pool) pool.style.opacity = '0';
  if (startBtn) startBtn.style.display = 'none';

  selectedStancePair = { a: 'FOR', b: 'AGAINST' };
  selectedPersonas.host = null;
  selectedPersonas.debaterA = null;
  selectedPersonas.debaterB = null;

  // Clear cached tags/professions so skeleton shows fresh
  personaTags = {};
  personaProfessions = {};

  if (chyron) chyron.textContent = topic;

  // Render pool immediately with skeleton placeholders
  renderControlRoomPool();
  if (pool) {
    pool.style.transition = 'opacity 0.3s ease';
    pool.style.opacity = '1';
  }
  if (startBtn) startBtn.style.display = '';

  // Show skeleton shimmer on stance labels
  if (labelLeft) { labelLeft.textContent = '\u00A0'; labelLeft.classList.add('shimmer'); }
  if (labelRight) { labelRight.textContent = '\u00A0'; labelRight.classList.add('shimmer'); }

  // Fire stances + persona tags in parallel — pool cards update in-place as data arrives
  const stanceUrl = `/api/topics/stances?topic=${encodeURIComponent(topic)}${selectedContext ? `&context=${encodeURIComponent(selectedContext)}` : ''}`;
  const stancePromise = fetch(stanceUrl)
    .then(r => r.json() as Promise<{ pairs?: StancePair[], slug?: string }>)
    .then(data => {
      if (data.pairs && data.pairs.length > 0) {
        selectedStancePair = data.pairs[0];
      }
      if (data.slug && selectedStancePair) selectedStancePair.slug = data.slug;
      if (labelLeft) { labelLeft.textContent = selectedStancePair!.a; labelLeft.classList.remove('shimmer'); }
      if (labelRight) { labelRight.textContent = selectedStancePair!.b; labelRight.classList.remove('shimmer'); }
    })
    .catch(err => {
      console.warn('Stance generation failed, using FOR/AGAINST:', err);
      if (labelLeft) { labelLeft.textContent = selectedStancePair!.a; labelLeft.classList.remove('shimmer'); }
      if (labelRight) { labelRight.textContent = selectedStancePair!.b; labelRight.classList.remove('shimmer'); }
    });

  const tagsPromise = fetchPersonaTags();

  await Promise.all([stancePromise, tagsPromise]);
}

async function fetchPersonaTags(): Promise<void> {
  if (!selectedTopic || !selectedStancePair) return;
  try {
    const res = await fetch('/api/topics/persona-tags', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        topic: selectedTopic,
        stanceA: selectedStancePair.a,
        stanceB: selectedStancePair.b,
        context: selectedContext,
      }),
    });
    const data = await res.json() as { tags?: Record<string, string>; professions?: Record<string, string> };
    if (data.tags) {
      personaTags = data.tags;
      personaProfessions = data.professions || {};
      for (const [id, tag] of Object.entries(personaTags)) {
        const el = document.querySelector(`.pool-card[data-persona-id="${id}"] .pool-card-tag`);
        if (el) { el.textContent = tag; el.classList.remove('shimmer'); }
      }
      for (const [id, prof] of Object.entries(personaProfessions)) {
        const el = document.querySelector(`.pool-card[data-persona-id="${id}"] .pool-card-profession`);
        if (el) { el.textContent = prof; el.classList.remove('shimmer'); }
      }
    }
  } catch (err) {
    console.warn('Persona tag fetch failed:', err);
  }
}

function renderControlRoomPool(): void {
  const container = $('#persona-pool');
  if (!container) return;
  container.innerHTML = '';

  $$('.monitor-screen').forEach((screen) => {
    screen.classList.remove('filled');
    const content = screen.querySelector('.monitor-content');
    if (content) content.innerHTML = '';
  });

  for (const p of PERSONAS) {
    const card = document.createElement('div');
    card.className = 'pool-card';
    card.dataset.personaId = p.id;
    const isTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    card.draggable = !isTouch;

    const avatar = document.createElement('img');
    avatar.className = 'pool-card-avatar';
    avatar.src = p.photo || `https://api.dicebear.com/9.x/notionists/svg?seed=${encodeURIComponent(p.name)}&backgroundColor=1a1a2e`;
    avatar.alt = p.name;

    const name = document.createElement('div');
    name.className = 'pool-card-name';
    name.textContent = p.name;

    const nickname = document.createElement('div');
    nickname.className = 'pool-card-nickname';
    nickname.textContent = `"${p.nickname}"`;
    nickname.style.color = p.color;

    const trait = document.createElement('div');
    trait.className = 'pool-card-trait';
    trait.textContent = p.trait.split('.').slice(0, 1).join('.') + '.';

    const profession = document.createElement('div');
    profession.className = 'pool-card-profession';
    if (personaProfessions[p.id]) {
      profession.textContent = personaProfessions[p.id];
    } else {
      profession.textContent = '\u00A0';
      profession.classList.add('shimmer');
    }

    const tag = document.createElement('div');
    tag.className = 'pool-card-tag';
    if (personaTags[p.id]) {
      tag.textContent = personaTags[p.id];
    } else {
      tag.textContent = '\u00A0';
      tag.classList.add('shimmer');
    }

    const badge = document.createElement('div');
    badge.className = 'pool-card-badge';
    badge.textContent = 'CAST';

    card.appendChild(avatar);
    card.appendChild(name);
    card.appendChild(nickname);
    card.appendChild(profession);
    card.appendChild(trait);
    card.appendChild(tag);
    card.appendChild(badge);

    card.addEventListener('dragstart', (e) => {
      draggedPersonaId = p.id;
      card.classList.add('dragging');
      e.dataTransfer!.effectAllowed = 'move';
      e.dataTransfer!.setData('text/plain', p.id);
    });

    card.addEventListener('dragend', () => {
      card.classList.remove('dragging');
      draggedPersonaId = null;
      $$('.control-room-monitor').forEach((m) => m.classList.remove('drag-over'));
    });

    card.addEventListener('click', () => {
      if (card.classList.contains('placed')) return;
      const wasSelected = card.classList.contains('tap-selected');
      $$('.pool-card').forEach((c) => c.classList.remove('tap-selected'));
      if (!wasSelected) {
        card.classList.add('tap-selected');
        draggedPersonaId = p.id;
      } else {
        draggedPersonaId = null;
      }
    });

    container.appendChild(card);
  }

  setupMonitorDropZones();
  updateControlRoomUI();
}

function setupMonitorDropZones(): void {
  $$('.control-room-monitor').forEach((monitor) => {
    const slot = monitor.dataset.slot as keyof typeof selectedPersonas;

    monitor.addEventListener('dragover', (e) => {
      e.preventDefault();
      (e as DragEvent).dataTransfer!.dropEffect = 'move';
      monitor.classList.add('drag-over');
    });

    monitor.addEventListener('dragleave', () => {
      monitor.classList.remove('drag-over');
    });

    monitor.addEventListener('drop', (e) => {
      e.preventDefault();
      monitor.classList.remove('drag-over');
      const personaId = (e as DragEvent).dataTransfer!.getData('text/plain') || draggedPersonaId;
      if (personaId) placePersonaInMonitor(personaId, slot);
    });

    monitor.addEventListener('click', () => {
      if (draggedPersonaId) {
        placePersonaInMonitor(draggedPersonaId, slot);
        $$('.pool-card').forEach((c) => c.classList.remove('tap-selected'));
        draggedPersonaId = null;
      } else if (selectedPersonas[slot]) {
        removePersonaFromMonitor(slot);
      }
    });
  });
}

function placePersonaInMonitor(personaId: string, slot: keyof typeof selectedPersonas): void {
  for (const s of ['host', 'debaterA', 'debaterB'] as const) {
    if (selectedPersonas[s] === personaId) {
      removePersonaFromMonitor(s);
    }
  }

  if (selectedPersonas[slot]) {
    removePersonaFromMonitor(slot);
  }

  selectedPersonas[slot] = personaId;
  const persona = PERSONAS.find((p) => p.id === personaId);
  if (!persona) return;

  const monitorId = slot === 'host' ? 'monitor-center' : slot === 'debaterA' ? 'monitor-left' : 'monitor-right';
  const monitor = $(`#${monitorId}`);
  if (!monitor) return;

  const screen = monitor.querySelector('.monitor-screen');
  const content = monitor.querySelector('.monitor-content');
  if (!screen || !content) return;

  const tag = personaTags[personaId] || '';

  content.innerHTML = '';

  const img = document.createElement('img');
  img.className = 'monitor-avatar';
  img.src = persona.photo || '';
  img.alt = persona.name;

  const nameplate = document.createElement('div');
  nameplate.className = 'monitor-nameplate';

  const nameEl = document.createElement('span');
  nameEl.className = 'monitor-nameplate-name';
  nameEl.textContent = persona.name;

  const tagEl = document.createElement('span');
  tagEl.className = 'monitor-nameplate-tag';
  tagEl.textContent = tag;

  nameplate.appendChild(nameEl);
  nameplate.appendChild(tagEl);
  content.appendChild(img);
  content.appendChild(nameplate);

  screen.classList.add('filled');
  updateControlRoomUI();
}

function removePersonaFromMonitor(slot: keyof typeof selectedPersonas): void {
  selectedPersonas[slot] = null;
  const monitorId = slot === 'host' ? 'monitor-center' : slot === 'debaterA' ? 'monitor-left' : 'monitor-right';
  const monitor = $(`#${monitorId}`);
  if (!monitor) return;

  const screen = monitor.querySelector('.monitor-screen');
  const content = monitor.querySelector('.monitor-content');
  if (screen) screen.classList.remove('filled');
  if (content) content.innerHTML = '';

  updateControlRoomUI();
}

function updateControlRoomUI(): void {
  $$('.pool-card').forEach((card) => {
    const id = card.dataset.personaId;
    const isPlaced = Object.values(selectedPersonas).includes(id!);
    card.classList.toggle('placed', isPlaced);
  });

  const startBtn = $('#start-btn') as HTMLButtonElement | null;
  const allSelected = selectedPersonas.host && selectedPersonas.debaterA && selectedPersonas.debaterB;
  if (startBtn) startBtn.disabled = !allSelected;
}

function getPersonasFromSelection(): { host: Persona | undefined; debaterA: Persona | undefined; debaterB: Persona | undefined } {
  const host = PERSONAS.find((p) => p.id === selectedPersonas.host);
  const debaterA = PERSONAS.find((p) => p.id === selectedPersonas.debaterA);
  const debaterB = PERSONAS.find((p) => p.id === selectedPersonas.debaterB);
  return { host, debaterA, debaterB };
}

// ═══════════════════════════════════════════════════════════
// Avatar Initialization
// ═══════════════════════════════════════════════════════════

async function initAvatars(): Promise<number> {
  avatarManager.registerVideoElement('Host', $('#tile-host .speaker-video') as HTMLVideoElement | null);
  avatarManager.registerVideoElement('DebaterA', $('#tile-a .speaker-video') as HTMLVideoElement | null);
  avatarManager.registerVideoElement('DebaterB', $('#tile-b .speaker-video') as HTMLVideoElement | null);

  try {
    const connected = await avatarManager.initialize((done, total) => {
      const bt = $('#connecting-text');
      if (bt) bt.textContent = `Connecting... ${done}/${total}`;
    }, {
      hostId: selectedPersonas.host || undefined,
      debaterAId: selectedPersonas.debaterA || undefined,
      debaterBId: selectedPersonas.debaterB || undefined,
    });
    avatarsEnabled = connected > 0;

    return connected;
  } catch (err) {
    console.warn('Avatar init failed:', err);
    avatarsEnabled = false;
    return 0;
  }
}

// ═══════════════════════════════════════════════════════════
// Start Podcast
// ═══════════════════════════════════════════════════════════

async function startPodcast(mode = 'normal'): Promise<void> {
  const modeModal = $('#mode-modal');
  if (modeModal) modeModal.classList.remove('active');

  const startBtn = $('#start-btn') as HTMLButtonElement | null;
  if (startBtn) {
    startBtn.disabled = true;
    startBtn.textContent = 'Initializing...';
  }

  const blocker = $('#connecting-blocker');
  const blockerText = $('#connecting-text');
  if (blocker) blocker.classList.add('active');
  if (blockerText) blockerText.textContent = 'Initializing...';

  const { host, debaterA, debaterB } = getPersonasFromSelection();
  const config = {
    topic: selectedTopic || 'Should AI replace human workers?',
    question: selectedTopic,
    stanceA: selectedStancePair?.a || 'FOR',
    stanceB: selectedStancePair?.b || 'AGAINST',
    host: { id: host!.id },
    debaterA: { id: debaterA!.id },
    debaterB: { id: debaterB!.id },
    mode,
    slug: selectedStancePair?.slug,
    context: selectedContext || undefined,
  };

  const configRes = await fetch('/api/podcast/config', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(config),
  });
  const { jobId } = await configRes.json() as { ok: boolean; jobId: string };

  try {
    const connected = await initAvatars();
    console.log(`[Podcast] ${connected} avatars connected`);
  } catch (err) {
    console.warn('Avatar init failed, continuing without:', err);
    avatarsEnabled = false;
  }

  // Start creative loading messages (blocker stays active while pre-gen runs)
  startLoadingCycle();

  const overlay = $('#start-overlay');
  if (overlay) overlay.classList.add('hidden');

  $('#camera-dropdown')?.classList.add('live');
  $('#settings-bar')?.classList.add('live');

  // dialIn runs behind the blocker — tiles animate while waiting
  await dialIn();

  const streamUrl = `/api/podcast/stream?job=${encodeURIComponent(jobId)}`;
  eventSource = new EventSource(streamUrl);

  eventSource.onmessage = (e) => {
    try {
      const event = JSON.parse(e.data) as PodcastEvent;
      enqueueEvent(event);
      if (event.type === 'done') {
        eventSource!.close();
      }
    } catch (err) {
      console.error('SSE parse error:', err);
    }
  };

  eventSource.onerror = () => {
    eventSource!.close();
    console.error('SSE connection lost');
  };
}


// ═══════════════════════════════════════════════════════════
// Init — Wire up event listeners
// ═══════════════════════════════════════════════════════════

// Context toggle
const ctxToggle = $('#context-toggle');
const ctxExpand = $('#context-expand');
const ctxTextarea = $('#topic-context') as HTMLTextAreaElement | null;
const ctxCharcount = $('#context-charcount');

ctxToggle?.addEventListener('click', (e) => {
  e.preventDefault();
  const isHidden = ctxExpand?.style.display === 'none';
  if (ctxExpand) ctxExpand.style.display = isHidden ? '' : 'none';
  if (ctxToggle) ctxToggle.textContent = isHidden ? '- Hide context' : '+ Add context';
  if (isHidden && ctxTextarea) ctxTextarea.focus();
});

ctxTextarea?.addEventListener('input', () => {
  selectedContext = ctxTextarea.value;
  if (ctxCharcount) ctxCharcount.textContent = `${ctxTextarea.value.length.toLocaleString()} / 2,000`;
});

$('#next-step-btn')?.addEventListener('click', goToStep2);
$('#back-step-btn')?.addEventListener('click', () => {
  selectedPersonas.host = null;
  selectedPersonas.debaterA = null;
  selectedPersonas.debaterB = null;
  selectedStancePair = null;
  showStep(1);
});
$('#start-btn')?.addEventListener('click', () => {
  const modal = $('#mode-modal');
  if (modal) modal.classList.add('active');
});
$$('.mode-btn').forEach((btn) => {
  btn.addEventListener('click', () => startPodcast(btn.dataset.mode));
});
$('.mode-modal-backdrop')?.addEventListener('click', () => {
  const modal = $('#mode-modal');
  if (modal) modal.classList.remove('active');
});

function toggleCaptions(): void {
  captionsEnabled = !captionsEnabled;
  $('#lower-third')?.classList.toggle('captions-on', captionsEnabled);
  $('#cc-btn')?.classList.toggle('cc-active', captionsEnabled);
}

/** Periodically flip lower-third to show the debate title for 2.5s */
function startTitleFlash(): void {
  stopTitleFlash();
  // Flash every 25-35s (randomized so it feels organic)
  const schedule = () => {
    const interval = 25000 + Math.random() * 10000;
    titleFlashInterval = setTimeout(() => {
      if (!captionsEnabled) { schedule(); return; }
      // Temporarily flip to title
      $('#lower-third')?.classList.remove('captions-on');
      titleFlashTimeout = setTimeout(() => {
        if (captionsEnabled) $('#lower-third')?.classList.add('captions-on');
        titleFlashTimeout = null;
        schedule();
      }, 2500);
    }, interval) as unknown as ReturnType<typeof setInterval>;
  };
  schedule();
}

function stopTitleFlash(): void {
  if (titleFlashInterval) { clearTimeout(titleFlashInterval as unknown as ReturnType<typeof setTimeout>); titleFlashInterval = null; }
  if (titleFlashTimeout) { clearTimeout(titleFlashTimeout); titleFlashTimeout = null; }
}

// Camera dropdown
(function initCameraDropdown() {
  const dropdown = $('#camera-dropdown');
  const menu = $('#camera-menu');
  if (!dropdown || !menu) return;

  CAMERA_PRESETS.forEach((preset, i) => {
    const item = document.createElement('button');
    item.className = 'camera-menu-item' + (i === 0 ? ' active' : '');
    item.textContent = preset.label === 'auto' ? 'Auto' : preset.label;
    item.addEventListener('click', (e) => {
      e.stopPropagation();
      selectCameraPreset(i);
      menu.querySelectorAll('.camera-menu-item').forEach((el, j) => el.classList.toggle('active', j === i));
      dropdown.classList.remove('open');
    });
    menu.appendChild(item);
  });

  $('#camera-btn')?.addEventListener('click', () => dropdown.classList.toggle('open'));

  document.addEventListener('click', (e) => {
    if (!dropdown.contains(e.target as Node)) dropdown.classList.remove('open');
  });
})();
$('#refresh-topics-btn')?.addEventListener('click', refreshTopics);

$('#lower-third')?.addEventListener('click', toggleCaptions);
$('#cc-btn')?.addEventListener('click', (e) => { e.stopPropagation(); toggleCaptions(); });
initTopicCards();

$('#custom-topic')?.addEventListener('input', () => {
  const input = $('#custom-topic') as HTMLInputElement | null;
  const btn = $('#next-step-btn') as HTMLButtonElement | null;
  const val = input?.value.trim();

  if (btn) btn.disabled = !val;

  $$('.topic-card').forEach((c) => c.classList.remove('selected'));
});

$('#custom-topic')?.addEventListener('paste', () => {
  setTimeout(() => {
    const val = ($('#custom-topic') as HTMLInputElement | null)?.value.trim();
    if (val && URL_PATTERN.test(val)) {
      extractTopicFromUrl(val);
    }
  }, 0);
});

$('#custom-topic')?.addEventListener('keydown', (e) => {
  if ((e as KeyboardEvent).key === 'Enter' && ($('#custom-topic') as HTMLInputElement | null)?.value.trim()) {
    goToStep2();
  }
});

// Cleanup on unload
window.addEventListener('beforeunload', () => {
  avatarManager.destroy().catch(() => {});
});

// ═══════════════════════════════════════════════════════════
// Replay Mode — /replay/:slug
// ═══════════════════════════════════════════════════════════

const replayMatch = location.pathname.match(/^\/replay\/([^/]+)$/);
if (replayMatch) {
  const slug = replayMatch[1];
  (async () => {
    // Hide setup overlay, show blocker while loading data
    const overlay = $('#start-overlay');
    if (overlay) overlay.classList.add('hidden');

    const blocker = $('#connecting-blocker');
    const blockerText = $('#connecting-text');
    if (blocker) blocker.classList.add('active');
    if (blockerText) blockerText.textContent = 'Loading debate...';

    try {
      const res = await fetch(`/api/debate/${encodeURIComponent(slug)}`);
      if (!res.ok) throw new Error('Debate not found');

      const { config, events } = await res.json() as {
        config: DebateConfig;
        events: PodcastEvent[];
      };

      // Set up state from config so setup handler works
      selectedTopic = config.topic;
      selectedStancePair = { a: config.stanceA, b: config.stanceB };
      selectedPersonas.host = config.host.id;
      selectedPersonas.debaterA = config.debaterA.id;
      selectedPersonas.debaterB = config.debaterB.id;

      // Fetch persona tags for professions display
      try {
        const tagsRes = await fetch('/api/topics/persona-tags', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            topic: config.topic,
            stanceA: config.stanceA,
            stanceB: config.stanceB,
          }),
        });
        const tagsData = await tagsRes.json() as {
          tags?: Record<string, string>;
          professions?: Record<string, string>;
        };
        if (tagsData.tags) personaTags = tagsData.tags;
        if (tagsData.professions) personaProfessions = tagsData.professions;
      } catch {
        // Non-critical — replay works without profession tags
      }

      // Build broadcast promo card — user gesture required for audio/avatar playback
      const spinner = blocker?.querySelector('.connecting-spinner') as HTMLElement | null;
      if (spinner) spinner.style.display = 'none';
      if (blockerText) blockerText.style.display = 'none';

      const content = blocker?.querySelector('.connecting-blocker-content');
      if (content) content.innerHTML = '';

      const personaA = PERSONAS.find(p => p.id === config.debaterA.id);
      const personaB = PERSONAS.find(p => p.id === config.debaterB.id);

      const promo = document.createElement('div');
      promo.className = 'replay-promo';
      promo.innerHTML = `
        <div class="replay-promo-brand">THE GREAT LIVE AI DEBATE</div>
        <h1 class="replay-promo-topic">${config.topic}</h1>
        <div class="replay-promo-matchup">
          <div class="replay-promo-debater replay-promo-debater--for">
            <img class="replay-promo-photo" src="${personaA?.photo || ''}" alt="${personaA?.name || 'Debater A'}" />
            <span class="replay-promo-name">${personaA?.name || 'Debater A'}</span>
            <span class="replay-promo-stance replay-promo-stance--for">FOR</span>
          </div>
          <div class="replay-promo-vs">VS</div>
          <div class="replay-promo-debater replay-promo-debater--against">
            <img class="replay-promo-photo" src="${personaB?.photo || ''}" alt="${personaB?.name || 'Debater B'}" />
            <span class="replay-promo-name">${personaB?.name || 'Debater B'}</span>
            <span class="replay-promo-stance replay-promo-stance--against">AGAINST</span>
          </div>
        </div>
        <button class="replay-watch-btn">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><polygon points="5 3 19 12 5 21 5 3"/></svg>
          <span>Watch Replay</span>
        </button>
        <a href="/" class="replay-home-link">Start Your Own Debate</a>
      `;
      content?.appendChild(promo);

      const watchBtn = promo.querySelector('.replay-watch-btn') as HTMLButtonElement;

      watchBtn.addEventListener('click', async () => {
        promo.remove();

        // Restore spinner + text (innerHTML='' destroyed the originals)
        const newSpinner = document.createElement('div');
        newSpinner.className = 'connecting-spinner';
        const newText = document.createElement('div');
        newText.className = 'connecting-text';
        newText.id = 'connecting-text';
        content?.appendChild(newSpinner);
        content?.appendChild(newText);

        // Show the same creative loading messages as a live debate
        startLoadingCycle();

        // Init avatars (after user gesture so audio is allowed)
        try {
          const connected = await initAvatars();
          console.log(`[Replay] ${connected} avatars connected`);
        } catch (err) {
          console.warn('[Replay] Avatar init failed, using TTS:', err);
          avatarsEnabled = false;
        }

        if (!avatarsEnabled) {
          const notice = document.createElement('div');
          notice.className = 'replay-tts-notice';
          notice.textContent = 'Using browser voice fallback — avatar service unavailable';
          document.body.appendChild(notice);
          setTimeout(() => notice.remove(), 6500);
        }

        $('#camera-dropdown')?.classList.add('live');
        $('#settings-bar')?.classList.add('live');

        await dialIn();

        stopLoadingCycle();

        // Remove blocker
        if (blocker) blocker.classList.remove('active');

        // Feed all events into the queue
        for (const event of events) {
          if (event.type === 'share_ready') continue;
          enqueueEvent(event);
        }
      });
    } catch (err) {
      console.error('[Replay] Failed:', err);
      if (blockerText) blockerText.textContent = 'Debate not found';
    }
  })();
}

// ═══════════════════════════════════════════════════════════
// Debug: auto-start with ?debug=closing
// ═══════════════════════════════════════════════════════════

if (new URLSearchParams(location.search).get('debug') === 'closing') {
  (async () => {
    selectedTopic = 'Should AI replace human teachers?';
    selectedStancePair = { a: 'EMPOWER', b: 'ENDANGER' };
    selectedPersonas.host = PERSONAS[0].id;
    selectedPersonas.debaterA = PERSONAS[1].id;
    selectedPersonas.debaterB = PERSONAS[2].id;

    const config = {
      topic: selectedTopic,
      question: selectedTopic,
      stanceA: selectedStancePair.a,
      stanceB: selectedStancePair.b,
      host: { id: selectedPersonas.host },
      debaterA: { id: selectedPersonas.debaterA },
      debaterB: { id: selectedPersonas.debaterB },
    };

    const debugConfigRes = await fetch('/api/podcast/config', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(config),
    });
    const { jobId: debugJobId } = await debugConfigRes.json() as { ok: boolean; jobId: string };

    const overlay = $('#start-overlay');
    if (overlay) overlay.classList.add('hidden');

    await dialIn();

    eventSource = new EventSource(`/api/podcast/stream?job=${encodeURIComponent(debugJobId)}`);
    eventSource.onmessage = (e) => {
      try {
        const event = JSON.parse(e.data) as PodcastEvent;
        enqueueEvent(event);
        if (event.type === 'done') eventSource!.close();
      } catch (err) {
        console.error('[Debug] SSE parse error:', err);
      }
    };
  })();
}
